import { ReactElement, useRef, useCallback, useMemo } from 'react';
import { Polygon } from 'geojson';
import mapboxgl from 'mapbox-gl';
import Map, {
  MapRef,
  FullscreenControl,
  NavigationControl,
  Popup,
  Source,
  Layer,
} from 'react-map-gl';
import { compact } from 'lodash';

import { Nullable, MapPopupData } from '~globals/types/commons';
import { usePopupRequest } from '~hooks/usePopupRequest';
import { usePopupData } from '~hooks/usePopupData';
import { getItemInfo } from '~services/item';
import { ApiTransactionVehicle } from '~services/apiTransaction/types';
import {
  ShowTripViewControl,
  ModeSateliteControl,
} from '~components/MapCustomControl';
import MarkerDeliveryType from '~components/MarkerDeliveryType';
import MarkerDeliveryVehicle from '~components/MarkerDeliveryVehicle';
import VehiclePopupMap from '~components/VehiclePopupMap';
import { VehiclePopupMapProps } from '~components/VehiclePopupMap/types';
import DialogDetailsOrder from '~components/DialogDetailsOrder';
import { TOKEN } from '~utils/mapbox';
import { controlLocale } from '~constants/mapControlLocale';

import { useDashboardVariantV2CarrierContentInTransitTabContext } from '../../../Context';
import { useDashboardVariantV2CarrierContentInTransitTrackingContext } from '../Context';
// eslint-disable-next-line max-len
import DashboardVariantV2CarrierContentInWarehouseTabMapPopup from '../../../../InWarehouse/Components/Map/Popup';

import { getZonePolygonFeature } from './utils';
import {
  DashboardVariantV2CarrierContentInTransitTabTrackingMapContainer,
  DashboardVariantV2CarrierContentInTransitTabTrackingMapLoading,
} from './styles';

const DashboardVariantV2CarrierContentInTransitTabTrackingMap =
  (): ReactElement => {
    const {
      dialogs: {
        detailsOrder: {
          data: selectedDataForDetailsOrder,
          open: openDialogDetailsOrder,
          handleOpen: handleOpenDialogDetailsOrder,
          handleClose: handleCloseDialogDetailsOrder,
        },
      },
    } = useDashboardVariantV2CarrierContentInTransitTabContext();

    const {
      tabsData: { loading: loadingTabData },
      mapData: {
        loading: loadingMapData,
        items,
        vehicles,
        logisticZones,
        currentRouteTrip,
        currentRouteTracking,
      },
    } = useDashboardVariantV2CarrierContentInTransitTrackingContext();

    const mapRef = useRef<Nullable<MapRef>>(null);

    const hasData = useMemo(() => {
      const dataCompact = compact([
        items.length,
        vehicles.length,
        logisticZones.length,
        currentRouteTrip.length,
        currentRouteTracking.length,
      ]);

      return dataCompact.length > 0;
    }, [
      items.length,
      vehicles.length,
      logisticZones.length,
      currentRouteTrip.length,
      currentRouteTracking.length,
    ]);

    const bounds = useMemo(() => {
      const currentBounds = new mapboxgl.LngLatBounds();

      if (items.length > 0) {
        items.forEach((item) => {
          if (item.coordinates.length > 1) {
            currentBounds.extend(item.coordinates);
          }
        });
      }

      if (vehicles.length > 0) {
        vehicles.forEach((vehicle) => {
          if (vehicle.longitude && vehicle.latitude) {
            currentBounds.extend(
              new mapboxgl.LngLat(vehicle.longitude, vehicle.latitude),
            );
          }
        });
      }

      if (logisticZones.length > 0) {
        logisticZones.forEach((logisticZone) => {
          if (logisticZone.geometryAreaJson) {
            const zonePolygon: Polygon = JSON.parse(
              logisticZone.geometryAreaJson,
            );

            zonePolygon.coordinates[0].forEach((coord) => {
              const polygonCoords = new mapboxgl.LngLat(coord[0], coord[1]);

              currentBounds.extend(polygonCoords);
            });
          }
        });
      }

      if (currentRouteTrip.length > 0) {
        currentRouteTrip.forEach((tripCoords) => {
          if (tripCoords.length > 1) {
            currentBounds.extend(
              new mapboxgl.LngLat(tripCoords[0], tripCoords[1]),
            );
          }
        });
      }

      if (currentRouteTracking.length > 0) {
        currentRouteTracking.forEach((trackingCoords) => {
          if (trackingCoords.length > 1) {
            currentBounds.extend(
              new mapboxgl.LngLat(trackingCoords[0], trackingCoords[1]),
            );
          }
        });
      }

      if (!currentBounds.isEmpty()) {
        mapRef.current?.fitBounds(currentBounds, { padding: 100, maxZoom: 12 });
      }

      return currentBounds;
    }, [
      items,
      vehicles,
      logisticZones,
      currentRouteTrip,
      currentRouteTracking,
    ]);

    const {
      handleOpenPopup: handleOpenPopupMarkerItem,
      handleClosePopup: handleClosePopupMarkerItem,
      ...restPopupOrder
    } = usePopupRequest({
      request: getItemInfo,
    });

    const {
      data: dataPopupVehicle,
      handleOpenPopup: handleOpenPopupMarkerVehicle,
      handleClosePopup: handleClosePopupMarkerVehicle,
    } = usePopupData<Nullable<MapPopupData<VehiclePopupMapProps>>>(null);

    const handleOpenPopupMarkerVehicleCallback = useCallback(
      (
        event: mapboxgl.MapboxEvent<MouseEvent>,
        vehicle: ApiTransactionVehicle,
      ) => {
        if (vehicle.longitude === null || vehicle.latitude === null) return;

        handleOpenPopupMarkerVehicle(event, {
          coordinates: {
            latitude: vehicle.latitude,
            longitude: vehicle.longitude,
          },
          data: {
            vehicle: {
              name: vehicle.name,
              referenceCode: vehicle.referenceCode,
              speed: vehicle.speed,
            },
            driver: {
              // eslint-disable-next-line max-len
              name: `${vehicle?.lastDriver?.lastName}, ${vehicle?.lastDriver?.firstName}`,
              battery: vehicle.battery,
              lastGeoReferenceDateTime: vehicle.lastGeoReferenceDateTime,
              contactMobile: vehicle.lastDriver?.contactMobile ?? null,
              contactPhone: vehicle.lastDriver?.contactPhone ?? null,
            },
          },
        });
      },
      [handleOpenPopupMarkerVehicle],
    );

    return (
      <DashboardVariantV2CarrierContentInTransitTabTrackingMapContainer>
        {(loadingMapData || loadingTabData) && (
          <DashboardVariantV2CarrierContentInTransitTabTrackingMapLoading />
        )}

        {hasData && (
          <Map
            ref={mapRef}
            mapboxAccessToken={TOKEN}
            mapStyle="mapbox://styles/mapbox/light-v10"
            initialViewState={{
              bounds,
              fitBoundsOptions: {
                padding: 100,
                maxZoom: 12,
              },
            }}
            attributionControl={false}
            locale={controlLocale}
          >
            <FullscreenControl position="bottom-left" />
            <NavigationControl position="bottom-left" showCompass={false} />
            {bounds && (
              <ShowTripViewControl
                position="bottom-left"
                currentBounds={bounds}
              />
            )}
            <ModeSateliteControl position="bottom-left" />

            {items.map((item, itemIndex) => (
              <MarkerDeliveryType
                key={`marker-item-${item.itemId}-${itemIndex}`}
                sourceImportTypeId={item.sourceImportTypeId}
                longitude={item.coordinates[0]}
                latitude={item.coordinates[1]}
                onClick={(event) =>
                  handleOpenPopupMarkerItem(
                    event,
                    item.itemId,
                    item.coordinates,
                  )
                }
                anchor="bottom"
                style={{ cursor: 'pointer' }}
              />
            ))}

            <DashboardVariantV2CarrierContentInWarehouseTabMapPopup
              {...restPopupOrder}
              onOpen={handleClosePopupMarkerVehicle}
              handleClosePopup={handleClosePopupMarkerItem}
              isItem
              onShowDetailOrder={handleOpenDialogDetailsOrder}
            />

            {vehicles.map(
              (vehicle, vehicleIndex) =>
                vehicle.longitude !== null &&
                vehicle.latitude !== null && (
                  <MarkerDeliveryVehicle
                    key={`marker-vehicle-${vehicle.vehicleId}-${vehicleIndex}`}
                    vehicleName={vehicle.name}
                    vehicleColor={vehicle.color}
                    driverName={
                      vehicle.lastDriver
                        ? `${vehicle.lastDriver.lastName}, ${vehicle.lastDriver.firstName}`
                        : null
                    }
                    longitude={vehicle.longitude}
                    latitude={vehicle.latitude}
                    onClick={(event) =>
                      handleOpenPopupMarkerVehicleCallback(event, vehicle)
                    }
                    anchor="bottom"
                    style={{ cursor: 'pointer' }}
                  />
                ),
            )}

            {dataPopupVehicle && (
              <Popup
                longitude={dataPopupVehicle.coordinates.longitude}
                latitude={dataPopupVehicle.coordinates.latitude}
                onOpen={handleClosePopupMarkerItem}
                onClose={handleClosePopupMarkerVehicle}
                offset={{
                  top: [0, 0],
                  bottom: [0, -30],
                  left: [15, -18],
                  right: [-15, -18],
                }}
              >
                <VehiclePopupMap {...dataPopupVehicle.data} />
              </Popup>
            )}

            {logisticZones.map((zone) => (
              <Source
                key={zone.zoneId}
                id={`zone-${zone.zoneId}`}
                type="geojson"
                data={getZonePolygonFeature(zone)}
              >
                <Layer
                  id={`zone-${zone.zoneId}-fill`}
                  type="fill"
                  paint={{
                    'fill-color': ['get', 'currentColor'],
                    'fill-outline-color': ['get', 'currentColor'],
                    'fill-opacity': 0.1,
                  }}
                />

                <Layer
                  id={`zone-${zone.zoneId}-line`}
                  type="line"
                  layout={{
                    'line-join': 'round',
                    'line-cap': 'round',
                  }}
                  paint={{
                    'line-color': ['get', 'currentColor'],
                    'line-width': 2,
                  }}
                />

                <Layer
                  id={`zone-${zone.zoneId}-label`}
                  type="symbol"
                  layout={{
                    'text-field': ['get', 'label'],
                    'text-size': 12,
                    'text-offset': [0, 1.25],
                    'text-anchor': 'center',
                  }}
                  paint={{
                    'text-color': ['get', 'currentColor'],
                  }}
                />
              </Source>
            ))}

            {currentRouteTrip.length > 0 && (
              <Source
                id="current-trip"
                type="geojson"
                data={{
                  type: 'Feature',
                  properties: {},
                  geometry: {
                    type: 'LineString',
                    coordinates: currentRouteTrip,
                  },
                }}
              >
                <Layer
                  id="current-trip"
                  type="line"
                  layout={{
                    'line-join': 'round',
                    'line-cap': 'round',
                  }}
                  paint={{
                    'line-color': '#212121',
                    'line-width': 1.5,
                  }}
                />
                <Layer
                  id="current-trip-arrow"
                  type="symbol"
                  layout={{
                    'symbol-placement': 'line',
                    'text-field': '▶',
                    'text-size': [
                      'interpolate',
                      ['linear'],
                      ['zoom'],
                      12,
                      10,
                      22,
                      60,
                    ],
                    'symbol-spacing': [
                      'interpolate',
                      ['linear'],
                      ['zoom'],
                      12,
                      15,
                      22,
                      160,
                    ],
                    'text-keep-upright': false,
                  }}
                  paint={{
                    'text-color': '#212121',
                  }}
                />
              </Source>
            )}

            {currentRouteTracking.length > 0 && (
              <Source
                id="current-tracking"
                type="geojson"
                data={{
                  type: 'Feature',
                  properties: {},
                  geometry: {
                    type: 'LineString',
                    coordinates: currentRouteTracking,
                  },
                }}
              >
                <Layer
                  id="current-tracking"
                  type="line"
                  layout={{
                    'line-cap': 'round',
                  }}
                  paint={{
                    'line-color': '#5618B6',
                    'line-width': 2,
                    'line-dasharray': [0, 2],
                  }}
                />

                <Layer
                  id="current-tracking-arrow"
                  type="symbol"
                  layout={{
                    'symbol-placement': 'line',
                    'text-field': '▶',
                    'text-size': [
                      'interpolate',
                      ['linear'],
                      ['zoom'],
                      12,
                      10,
                      22,
                      60,
                    ],
                    'symbol-spacing': [
                      'interpolate',
                      ['linear'],
                      ['zoom'],
                      12,
                      15,
                      22,
                      160,
                    ],
                    'text-keep-upright': false,
                  }}
                  paint={{
                    'text-color': '#5618B6',
                  }}
                />
              </Source>
            )}
          </Map>
        )}

        {openDialogDetailsOrder && selectedDataForDetailsOrder && (
          <DialogDetailsOrder
            open={openDialogDetailsOrder}
            onClose={handleCloseDialogDetailsOrder}
            enabledChipTitle
            {...selectedDataForDetailsOrder}
          />
        )}
      </DashboardVariantV2CarrierContentInTransitTabTrackingMapContainer>
    );
  };

export default DashboardVariantV2CarrierContentInTransitTabTrackingMap;
